import React from 'react';

import { useIntl } from 'react-intl-next';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import Form from '@atlaskit/form';
import WarningIcon from '@atlaskit/icon/core/migration/warning';
import { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { Text } from '@atlaskit/primitives/compiled';
import { Y400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { Flex } from '@atlassian/eoc-flex';

import { messages as commonMessages } from '../../../common/messages';
import { Bold } from '../../../common/ui/bold/styled';
import { useDeleteRotationService } from '../../../controllers/edit';
import { useSchedule } from '../../../controllers/schedule-store/helpers';

import { messages } from './messages';
import { type DeleteRotationProps } from './types';

// TODO: Should disable "cancel" button when delete is loading ? (same goes for other modals!)
// Image while rotation is being deleted, the modal is closed ? It shouldn't happen
// We should disable while loading! Remove onClose from modal, handle close by 'ESC' inside components
// Use useOnEsc(action) like hook. This hook can be also used on focus state, and period items.
// Hooks implementation should be based on atlaskit/modal esc functionality.
const DeleteRotation: React.FC<DeleteRotationProps> = ({ onClose, baseRotation }) => {
	const intlApi = useIntl();
	const [{ id: scheduleId }] = useSchedule();
	const { deleteRotation, isLoading } = useDeleteRotationService(scheduleId, baseRotation.id);

	return (
		<Form
			onSubmit={() => {
				deleteRotation();
			}}
		>
			{({ formProps }) => (
				<form {...formProps}>
					<ModalHeader>
						<Flex alignItems="center" columnGap={1}>
							<WarningIcon
								spacing="spacious"
								label="warning"
								color={token('color.icon.warning', Y400)}
							/>
							<ModalTitle>{intlApi.formatMessage(messages.title)}</ModalTitle>
						</Flex>
					</ModalHeader>
					<ModalBody>
						<Text as="p">
							{intlApi.formatMessage(messages.explanation, {
								bold: (str: React.ReactNode) => <Bold>{str}</Bold>,
								rotationName: baseRotation.name,
							})}
						</Text>
						<Text as="p">{intlApi.formatMessage(commonMessages.confirmProceed)}</Text>
					</ModalBody>
					<ModalFooter>
						<ButtonGroup>
							<Button appearance="subtle" onClick={onClose}>
								{intlApi.formatMessage(commonMessages.cancel)}
							</Button>
							<Button autoFocus appearance="warning" isLoading={isLoading} type="submit">
								{intlApi.formatMessage(commonMessages.delete)}
							</Button>
						</ButtonGroup>
					</ModalFooter>
				</form>
			)}
		</Form>
	);
};

export { DeleteRotation };
